/**
 * Online Food Ordering Header Component
 * Responsive app bar with search, cart, and user menu
 */

import {
  ShoppingCart as CartIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Logout as LogoutIcon,
  Restaurant as MenuIcon,
  Receipt as OrdersIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  AppBar,
  Avatar,
  alpha,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { useAppStore } from '@/store/useAppStore';
import { useAuthStore } from '@/store/useAuthStore';
import { useCartItemCount } from '@/store/useCartStore';
import { tn } from '@/utils/i18n';

export default function Header() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const restaurant = useAppStore((state) => state.restaurant);
  const themeMode = useAppStore((state) => state.themeMode);
  const toggleTheme = useAppStore((state) => state.toggleTheme);
  const toggleCartDrawer = useAppStore((state) => state.toggleCartDrawer);
  const language = useAppStore((state) => state.language);
  const setLanguage = useAppStore((state) => state.setLanguage);

  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const logout = useAuthStore((state) => state.logout);

  const cartItemCount = useCartItemCount();

  const handleLogout = () => {
    logout();
    setAnchorEl(null);
    navigate('/');
  };

  return (
    <AppBar
      position="fixed"
      color="inherit"
      sx={{
        backdropFilter: 'blur(8px)',
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
      }}
    >
      <Toolbar sx={{ gap: 1 }}>
        {/* Logo */}
        <Box
          component={RouterLink}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            mr: 2,
          }}
        >
          {restaurant?.logo_url && restaurant.logo_url.trim() !== '' && (
            <Box
              component="img"
              src={restaurant.logo_url}
              alt={tn(restaurant.name_translations)}
              sx={{
                height: { xs: 40, sm: 50, md: 56 },
                width: 'auto',
                maxWidth: { xs: 120, sm: 150, md: 180 },
                objectFit: 'contain',
                mr: 1,
                backgroundColor: '#ffffff !important',
                borderRadius: 0.5,
                padding: 0.5,
              }}
            />
          )}
          <Typography
            variant="h6"
            component="span"
            noWrap
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              fontSize: { xs: 'clamp(0.75rem, 4vw, 1.1rem)', sm: '1.25rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: { xs: '45vw', sm: 'none' },
            }}
          >
            {(language === 'ar' && restaurant?.name_translations?.ar) ||
            restaurant?.name_translations?.en ||
            (language === 'ar' ? 'مطبخ سلطان الخليج' : 'Sulthan Gulf Kitchen')}
          </Typography>
        </Box>

        {/* Desktop Navigation */}
        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component={RouterLink}
              to="/menu"
              startIcon={<MenuIcon />}
              sx={{ color: 'text.primary' }}
            >
              {t('nav.menu')}
            </Button>
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Language Toggle */}
        <ToggleButtonGroup
          value={language}
          exclusive
          onChange={(_, v) => v && setLanguage(v as 'en' | 'ar')}
          size="small"
          sx={{ mr: 0.5 }}
        >
          <ToggleButton
            value="en"
            sx={{ px: 1.5, py: 0.5, fontWeight: 600, fontSize: '0.75rem', minWidth: 36 }}
          >
            EN
          </ToggleButton>
          <ToggleButton
            value="ar"
            sx={{ px: 1.5, py: 0.5, fontWeight: 600, fontSize: '0.85rem', minWidth: 36 }}
          >
            ع
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Theme Toggle */}
        <IconButton onClick={toggleTheme} color="inherit">
          {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>

        {/* Cart Button */}
        <IconButton
          color="primary"
          onClick={toggleCartDrawer}
          sx={{
            backgroundColor: cartItemCount > 0 ? 'primary.main' : 'transparent',
            color: cartItemCount > 0 ? 'white' : 'primary.main',
            '&:hover': {
              backgroundColor:
                cartItemCount > 0 ? 'primary.dark' : alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          <Badge badgeContent={cartItemCount} color="error">
            <CartIcon />
          </Badge>
        </IconButton>

        {/* User Menu */}
        {isAuthenticated ? (
          <>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 1 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                }}
              >
                {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              onClick={() => setAnchorEl(null)}
              slotProps={{
                paper: {
                  elevation: 3,
                  sx: {
                    minWidth: 200,
                    mt: 1.5,
                    borderRadius: 2,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {user?.first_name || 'User'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem component={RouterLink} to="/orders">
                <ListItemIcon>
                  <OrdersIcon fontSize="small" />
                </ListItemIcon>
                {t('nav.orders')}
              </MenuItem>
              <MenuItem component={RouterLink} to="/profile">
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                {t('nav.settings')}
              </MenuItem>
              {user?.role && ['admin', 'super_admin'].includes(user.role) && (
                <MenuItem component={RouterLink} to="/admin">
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  {t('nav.admin_panel')}
                </MenuItem>
              )}
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                {t('nav.logout')}
              </MenuItem>
            </Menu>
          </>
        ) : (
          !isMobile && (
            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              size="small"
              sx={{ ml: 1 }}
            >
              {t('nav.sign_in')}
            </Button>
          )
        )}
      </Toolbar>
    </AppBar>
  );
}
